import React from 'react';
import {
  Typography,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import Chart from 'react-google-charts';

import { FONT_SIZES, PALETTE } from '~/assets/styles/base';
import { SummaryQuestionData, AnswerProps, SingleAnswerData } from '~/common/types/api/users';
import { QUESTION_TYPE_LABEL } from '~/common/constants';

const ContentBox = styled.div`
  width: 100%;
  padding: 25px;
  margin: 20px 0;
  border: 1px solid ${PALETTE.border.frame};
  background: ${PALETTE.white};
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const QuestionTitle = styled(Typography)`
  font-size: 20px;
  color: ${PALETTE.text.main};
  white-space: pre-line;
`;

const StyledTable = styled(Table)`
  border: 1px solid ${PALETTE.border.table};
`;

const StyledTableHeader = styled(TableCell)`
  background: ${PALETTE.blue.main};
  border: 1px solid ${PALETTE.border.table};
  text-align: center;
  color: ${PALETTE.text.main};
  font-size: ${FONT_SIZES.title4};
  padding: 7px 27px 7px 27px;
  height: 36px;
`;

const StyledTableCell = styled(TableCell)<StyledTableCellProps>`
  background: ${(props) =>
    props.$index !== undefined && props.$index % 2 === 0 ? PALETTE.white : PALETTE.blue.light};
  color: ${PALETTE.text.main};
  font-size: 16px;
  padding: 7px 27px 7px 27px;
  height: 36px;
`;

type StyledTableCellProps = {
  $index?: number;
};

type Props = {
  question: SummaryQuestionData;
  questionIdx: number;
};

const SingleAnswer: React.FC<Props> = ({ question, questionIdx }: Props) => {
  const { t } = useTranslation();

  const chartTitle: string[] = [];
  const chartValue: any[] = [];
  const chartData = [chartTitle, chartValue];
  const options = {
    isStacked: true,
    legend: { position: 'right' },
    tooltip: { trigger: 'both', isHtml: true },
    vAxis: {
      textPosition: 'none',
      gridlines: { color: 'transparent' },
    },
    animation: {
      duration: 500,
      easing: 'out',
      startup: true,
    },
  };

  const removeTags = (str: string) => {
    const regex = /<[^>]+>/g;
    return str.replace(regex, '');
  };

  let data: SingleAnswerData = { total: 0, answers: [] };
  if (question.singleAnswer) {
    data = question.singleAnswer;
    chartTitle.push('Base');
    chartValue.push(`Base: ${data.total}`);
    data.answers.map((answer) => {
      chartTitle.push(removeTags(answer.answerLabel));
      chartValue.push(answer.answerCount);
      return answer;
    });
  }

  return (
    <ContentBox>
      <QuestionTitle data-testid="question-label">
        {`${questionIdx + 1}. ${removeTags(question.questionLabel)} [${
          (QUESTION_TYPE_LABEL as Record<number, string>)[question.questionType]
        }]`}
      </QuestionTitle>
      <Chart chartType="BarChart" width="95%" height="400px" data={chartData} options={options} />
      <TableContainer sx={{ marginBottom: '30px' }} component="div" data-testid="question-table">
        <StyledTable>
          <TableHead data-testid="table-header">
            <TableRow>
              <StyledTableHeader sx={{ width: '490px' }}>
                {t('webAggregation.tableHeader.answer')}
              </StyledTableHeader>
              <StyledTableHeader sx={{ width: '205px' }}>
                {t('webAggregation.tableHeader.number')}
              </StyledTableHeader>
              <StyledTableHeader sx={{ width: '204px' }}>
                {t('webAggregation.tableHeader.percent')}
              </StyledTableHeader>
            </TableRow>
          </TableHead>

          <TableBody data-testid="question-table-body">
            <TableRow key="0">
              <StyledTableCell $index={0}>Base</StyledTableCell>
              <StyledTableCell $index={0} align="right">
                {data.total}
              </StyledTableCell>
              <StyledTableCell $index={0} align="right">
                100.0
              </StyledTableCell>
            </TableRow>
            {Array.isArray(data.answers) &&
              data.answers.map((answer: AnswerProps, answerIdx) => (
                <TableRow key={answerIdx}>
                  <StyledTableCell $index={answerIdx + 1}>
                    {removeTags(answer.answerLabel)}
                  </StyledTableCell>
                  <StyledTableCell $index={answerIdx + 1} align="right">
                    {answer.answerCount}
                  </StyledTableCell>
                  <StyledTableCell $index={answerIdx + 1} align="right">
                    {Math.round((answer.answerCount / data.total) * 100).toFixed(1)}
                  </StyledTableCell>
                </TableRow>
              ))}
          </TableBody>
        </StyledTable>
      </TableContainer>
    </ContentBox>
  );
};
export default SingleAnswer;
